import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  SvgIcon,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import { CustomInput } from "global/components";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import { notExpiryUpdatePwd } from "screens/Configurations/ConfigurationsService";
import { useAppSelector } from "hooks/use-app-select";
import securityStyles from "./Security.styles";
import securityTokens from "./Security.tokens";
import {
  PASSWORD_REQUIREMENTS,
  areAllPasswordRequirementsMet,
  getPasswordRequirements,
} from "./SecurityHelper";

const styles = securityStyles;

interface FieldState {
  value: string;
  error: string;
}

interface PasswordFields {
  oldPwd: FieldState;
  pwd: FieldState;
  rePassword: FieldState;
}

type SessionOption = "signout" | "stay";

const SESSION_OPTIONS: {
  value: SessionOption;
  title: string;
  description: string;
}[] = [
  {
    value: "signout",
    title: "Sign out other devices",
    description: "All other active sessions will be logged out immediately.",
  },
  {
    value: "stay",
    title: "Stay signed in everywhere",
    description: "Your password updates but all devices remain logged in.",
  },
];

const ChangePassword = () => {
  const { email } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState<boolean>(false);
  const [pwsFields, setPwdFields] = useState<PasswordFields>({
    oldPwd: { value: "", error: "" },
    pwd: { value: "", error: "" },
    rePassword: { value: "", error: "" },
  });
  const [sessionOption, setSessionOption] = useState<SessionOption>("signout");
  // Each field reveals independently, matching the login screen's toggle.
  const [visible, setVisible] = useState<Record<keyof PasswordFields, boolean>>(
    { oldPwd: false, pwd: false, rePassword: false },
  );

  const requirements = getPasswordRequirements(pwsFields.pwd.value);

  const validateField = (
    name: string,
    value: string,
    allValues: { oldPwd: string; pwd: string; rePassword: string },
  ): string => {
    switch (name) {
      case "oldPwd":
        if (!value) return "Please enter old password.";
        if (value.length < 8) return "Password must be 8 characters long";
        return "";
      case "pwd":
        if (!value) return "Please enter new password.";
        // Nothing stopped the new password from being the current one, so the
        // form stayed enabled and reported success on a no-op change.
        if (allValues.oldPwd && value === allValues.oldPwd)
          return "New password must be different from your current password.";
        return "";
      case "rePassword":
        if (!value) return "Please re-enter new password.";
        if (value !== allValues.pwd) return "Passwords don't match!";
        return "";
      default:
        return "";
    }
  };

  const handleOnChange = (e: any) => {
    const { name, value } = e.target;

    const allValues = {
      oldPwd: name === "oldPwd" ? value : pwsFields.oldPwd.value,
      pwd: name === "pwd" ? value : pwsFields.pwd.value,
      rePassword: name === "rePassword" ? value : pwsFields.rePassword.value,
    };

    const error = validateField(name, value, allValues);

    // When pwd changes, also re-validate rePassword live
    if (name === "pwd" && pwsFields.rePassword.value) {
      const rePasswordError = validateField(
        "rePassword",
        pwsFields.rePassword.value,
        allValues,
      );
      setPwdFields((prev) => ({
        ...prev,
        [name]: { value, error },
        rePassword: { ...prev.rePassword, error: rePasswordError },
      }));
      return;
    }

    // The new-password rule now reads the current password, so editing that
    // field has to re-check it -- otherwise a stale "must be different" error
    // would linger, or a newly-matching pair would go unflagged.
    if (name === "oldPwd" && pwsFields.pwd.value) {
      const pwdError = validateField("pwd", pwsFields.pwd.value, allValues);
      setPwdFields((prev) => ({
        ...prev,
        oldPwd: { value, error },
        pwd: { ...prev.pwd, error: pwdError },
      }));
      return;
    }

    setPwdFields((prev) => ({
      ...prev,
      [name]: { ...prev[name as keyof PasswordFields], value, error },
    }));
  };

  const pswValidation = () => {
    const allValues = {
      oldPwd: pwsFields.oldPwd.value,
      pwd: pwsFields.pwd.value,
      rePassword: pwsFields.rePassword.value,
    };

    const errors = {
      oldPwd: validateField("oldPwd", allValues.oldPwd, allValues),
      pwd: validateField("pwd", allValues.pwd, allValues),
      rePassword: validateField("rePassword", allValues.rePassword, allValues),
    };

    const hasErrors = Object.values(errors).some((e) => isTruthy(e));

    if (hasErrors) {
      setPwdFields((prev) => ({
        ...prev,
        oldPwd: { ...prev.oldPwd, error: errors.oldPwd },
        pwd: { ...prev.pwd, error: errors.pwd },
        rePassword: { ...prev.rePassword, error: errors.rePassword },
      }));
      return false;
    }

    return allValues;
  };

  const handleSubmit = async () => {
    try {
      const body = pswValidation();
      if (!body) return;
      setLoading(true);
      await notExpiryUpdatePwd(
        {
          email: email,
          pwd: body.pwd,
          oldPwd: body.oldPwd,
        },
        sessionOption === "signout",
      );
      setPwdFields({
        oldPwd: { value: "", error: "" },
        pwd: { value: "", error: "" },
        rePassword: { value: "", error: "" },
      });

      setSessionOption("signout");
      openSuccessNotification("Successfully updated password.");
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = (): boolean => {
    const allValues = {
      oldPwd: pwsFields.oldPwd.value,
      pwd: pwsFields.pwd.value,
      rePassword: pwsFields.rePassword.value,
    };

    const hasErrors =
      !!validateField("oldPwd", allValues.oldPwd, allValues) ||
      !!validateField("pwd", allValues.pwd, allValues) ||
      !!validateField("rePassword", allValues.rePassword, allValues);

    return !hasErrors && areAllPasswordRequirementsMet(allValues.pwd);
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  /** Eye toggle for a password box — same control the login screen uses. */
  const revealAdornment = (field: keyof PasswordFields, label: string) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label={`${visible[field] ? "Hide" : "Show"} ${label}`}
          onClick={() =>
            setVisible((prev) => ({ ...prev, [field]: !prev[field] }))
          }
          edge="end"
          tabIndex={0}
        >
          <SvgIcon>
            {visible[field] ? (
              <VisibilityOutlinedIcon />
            ) : (
              <VisibilityOffOutlinedIcon />
            )}
          </SvgIcon>
        </IconButton>
      </InputAdornment>
    ),
  });

  const getPasswordFields = () => (
    <Box sx={styles.fieldGrid}>
      <CustomInput
        id="oldPassword"
        label="Current Password"
        placeHolder="Enter current password"
        name="oldPwd"
        type={visible.oldPwd ? "text" : "password"}
        InputProps={revealAdornment("oldPwd", "current password")}
        required
        value={pwsFields.oldPwd.value}
        onChange={handleOnChange}
        onKeyPress={handleKeypress}
        propsToInputElement={{ maxLength: 30 }}
        error={!!pwsFields.oldPwd.error}
        helperText={pwsFields.oldPwd.error}
        labelSx={styles.fieldLabel}
        labelSpacing={securityTokens.field.labelGap}
        sx={styles.textField}
      />
      {/* The design leaves the cell beside "Current Password" empty. */}
      <Box sx={{ display: { xs: "none", md: "block" } }} />

      <CustomInput
        id="newpassword"
        label="New Password"
        placeHolder="Enter new password"
        name="pwd"
        type={visible.pwd ? "text" : "password"}
        InputProps={revealAdornment("pwd", "new password")}
        required
        value={pwsFields.pwd.value}
        onChange={handleOnChange}
        onKeyPress={handleKeypress}
        propsToInputElement={{ maxLength: 30 }}
        error={!!pwsFields.pwd.error}
        helperText={pwsFields.pwd.error}
        labelSx={styles.fieldLabel}
        labelSpacing={securityTokens.field.labelGap}
        sx={styles.textField}
      />
      <CustomInput
        id="repassword"
        label="Re-Enter New Password"
        placeHolder="Enter new password again"
        name="rePassword"
        type={visible.rePassword ? "text" : "password"}
        InputProps={revealAdornment("rePassword", "new password confirmation")}
        required
        value={pwsFields.rePassword.value}
        onChange={handleOnChange}
        onKeyPress={handleKeypress}
        propsToInputElement={{ maxLength: 30 }}
        error={!!pwsFields.rePassword.error}
        helperText={pwsFields.rePassword.error}
        labelSx={styles.fieldLabel}
        labelSpacing={securityTokens.field.labelGap}
        sx={styles.textField}
      />
    </Box>
  );

  const getRequirementsPanel = () => (
    <Box sx={styles.requirementsPanel}>
      {PASSWORD_REQUIREMENTS.map(({ key, label }) => {
        const met = requirements[key];
        return (
          <Box key={key} sx={styles.requirementRow(met)}>
            <Box sx={styles.requirementDot(met)} aria-hidden="true" />
            {label}
            {/* Color is not the only signal that a rule is (un)met. */}
            <Box component="span" sx={visuallyHidden}>
              {met ? " (met)" : " (not met)"}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const getSessionChoice = () => (
    <Box sx={styles.sessionChoiceGroup}>
      <Typography component="div" sx={styles.sessionChoiceLabel}>
        After updating, what should happen to your other devices?
      </Typography>
      <Box
        role="radiogroup"
        aria-label="After updating, what should happen to your other devices?"
        sx={styles.sessionChoiceRow}
      >
        {SESSION_OPTIONS.map(({ value, title, description }) => {
          const selected = sessionOption === value;
          return (
            <Box
              key={value}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setSessionOption(value)}
              sx={styles.sessionChoiceOption(selected)}
            >
              <Box sx={styles.sessionChoiceRadio(selected)}>
                <Box sx={styles.sessionChoiceRadioDot(selected)} />
              </Box>
              <Box sx={styles.sessionChoiceText}>
                <Box component="span" sx={styles.sessionChoiceTitle}>
                  {title}
                </Box>
                <Box component="span" sx={styles.sessionChoiceDescription}>
                  {description}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={styles.formCard}>
      <Box sx={styles.cardHeader}>
        <Typography component="h2" sx={styles.cardTitle}>
          Change Password
        </Typography>
        <Typography component="div" sx={styles.cardDescription}>
          Use at least 8 characters with a mix of letters, numbers, and symbols.
        </Typography>
      </Box>

      {getPasswordFields()}
      {getRequirementsPanel()}
      {getSessionChoice()}

      <Button
        id="update_password_btn"
        variant="contained"
        onClick={handleSubmit}
        disabled={loading || !isFormValid()}
        sx={styles.primaryButton}
      >
        {loading && (
          <CircularProgress
            size={securityTokens.primaryButton.spinnerSize}
            thickness={securityTokens.primaryButton.spinnerThickness}
            sx={styles.buttonSpinner}
          />
        )}
        Update Password
      </Button>
    </Box>
  );
};

export default ChangePassword;
